import { compareComplianceSeverity, compareComplianceCommentForWarning } from "../../compliance-profile-rules-association";
import { DATE_FORMAT_MM_DD_YYYY_HH_MM_SS_A, DATE_FORMAT_YYYY_MM_DD_HH_MM_SS_A, DateUtilities, DEFAULT_DATE_TIME_FORMAT, Utilities } from "../../../utilities";
import { SortOrder } from "../../gallery";
import { LocaleCodes, CurrencySymbols, defaultTranslateFn, EMPTY_TEXT, ARRAY } from "../../../common";
import { ComplianceProfileCommentForWarning, ConditionalRuleDataTypeEnum } from '../enums';
import { conditionalRuleConstants } from '../constants';
import { ComplianceProfileRuleSeverity } from "../../compliance-nested-rules";
export const getComplianceConditionalRulesBySearchText = (conditionalRules, searchText) => {
    if (searchText) {
        const text = searchText.toLocaleLowerCase();
        return conditionalRules.filter(({ ruleName, ruleDescription, createdBy, updatedBy, createdDate, updatedDate }) => [
            ruleName,
            ruleDescription,
            createdBy,
            updatedBy,
            getDateAndTime(createdDate),
            getDateAndTime(updatedDate)
        ].findIndex(field => field !== null && field.toLocaleLowerCase().includes(text)) !== -1);
    }
    return conditionalRules;
};
export const sortComplianceConditionalRules = (conditionalRules, sortId, sortDirection) => {
    if (!conditionalRules)
        return [];
    return conditionalRules.sort((prevConditionalRuleData, currentConditionalRuleData) => {
        let sortValue = sortId === conditionalRuleConstants.CONDITIONAL_RULE_SEVERITY_DATAKEY
            ? compareComplianceSeverity(prevConditionalRuleData.severity, currentConditionalRuleData.severity)
            : sortId === conditionalRuleConstants.CONDITIONAL_RULE_COMMENT_FOR_WARNING_DATAKEY
                ? compareComplianceCommentForWarning(prevConditionalRuleData.isCommentRequiredForOptional, currentConditionalRuleData.isCommentRequiredForOptional, prevConditionalRuleData.severity, currentConditionalRuleData.severity)
                : Utilities.compare(typeof prevConditionalRuleData[sortId] === 'string'
                    ? prevConditionalRuleData[sortId].trim()
                    : prevConditionalRuleData[sortId], typeof currentConditionalRuleData[sortId] === 'string'
                    ? currentConditionalRuleData[sortId].trim()
                    : currentConditionalRuleData[sortId]);
        return sortDirection === SortOrder.Ascending.toLowerCase() ? sortValue : -sortValue;
    });
};
export const mapArrayValueToGuidelineValue = (threshold) => {
    const guidelineValue = {
        options: [],
        selectedOptions: [],
        displayType: ''
    };
    if (threshold.thresholdVariable.dataType === ConditionalRuleDataTypeEnum.ARRAY) {
        threshold.additionalThreshold.guideline &&
            JSON.parse(threshold.additionalThreshold.guideline.value, function (key, value) {
                if (isNaN(Number(key))) {
                    guidelineValue[key.replace(key.split('')[0], key.split('')[0].toLowerCase())] = value;
                }
                return value;
            });
    }
    return guidelineValue;
};
export const handleExtraThreshold = (threshold) => {
    if (threshold.thresholdVariable.dataType === ConditionalRuleDataTypeEnum.ARRAY) {
        threshold.additionalThreshold = {
            guideline: getGuidelinesForThreshold(threshold),
            isDisabled: false,
            isOperatorDropdownRequired: true,
            isNumericTextboxRequired: true,
            isSelectionComponentRequired: true,
            isSelectionComponentCleared: false,
            selectedOperator: undefined // There is no operator for the array type thresholds
        };
    }
};
// create guidelines object passed binding in selection component
const getGuidelinesForThreshold = (threshold) => {
    return {
        options: null,
        override: false,
        isLookupEnabled: false,
        isHierarchyLookup: false,
        key: threshold.thresholdVariable.expressionId,
        value: threshold.thresholdVariable.value,
        dataType: {
            group: 8,
            name: conditionalRuleConstants.NONE,
            id: ARRAY
        }
    };
};
function parseJsonString(str) {
    try {
        return JSON.parse(str);
    }
    catch (e) {
        return {};
    }
}
export const getMinMaxNumericInputValue = (dataType, thresholds, cardTitle, translateFn = defaultTranslateFn) => {
    const numericInputValidation = {
        min: 0,
        max: 99999.99,
        step: 0.01,
        regex: conditionalRuleConstants.REGEX_FOR_CURRENCY
    };
    switch (dataType) {
        case ConditionalRuleDataTypeEnum.CURRENCY:
            const isPercentSelected = thresholds &&
                thresholds.length > 0 &&
                thresholds.some(threshold => {
                    var _a;
                    return threshold.thresholdVariable.value &&
                        ((_a = parseJsonString(threshold.thresholdVariable.value).selectedOptions) === null || _a === void 0 ? void 0 : _a.includes('%'));
                });
            if (isPercentSelected) {
                numericInputValidation.max = 99.9999;
                numericInputValidation.step = 0.0001;
                numericInputValidation.regex = conditionalRuleConstants.REGEX_FOR_PERCENT_IN_BETTERMENT;
                return numericInputValidation;
            }
            else
                return numericInputValidation;
        case ConditionalRuleDataTypeEnum.HOURS:
            numericInputValidation.max = 99.9;
            numericInputValidation.step = 0.1;
            numericInputValidation.regex = conditionalRuleConstants.REGEX_FOR_HOURS;
            return numericInputValidation;
        case ConditionalRuleDataTypeEnum.PERCENT:
            numericInputValidation.regex = conditionalRuleConstants.REGEX_FOR_MARK_DISC_PERCENT;
            if (cardTitle === translateFn(conditionalRuleConstants.PART_PRICE_COMPARISON_TITLE)) {
                const isDollarSelected = thresholds &&
                    thresholds.length > 0 &&
                    thresholds.some(threshold => {
                        var _a;
                        return threshold.thresholdVariable.value &&
                            ((_a = parseJsonString(threshold.thresholdVariable.value).selectedOptions) === null || _a === void 0 ? void 0 : _a.includes(conditionalRuleConstants.DOLLAR_RADIO));
                    });
                if (isDollarSelected) {
                    numericInputValidation.regex = conditionalRuleConstants.REGEX_FOR_CURRENCY;
                    return numericInputValidation;
                }
                numericInputValidation.max = 999.9999;
                numericInputValidation.step = 0.0001;
                return numericInputValidation;
            }
            if (cardTitle === translateFn(conditionalRuleConstants.PRE_TAX_DISCOUNT_TITLE)) {
                numericInputValidation.max = 99.99;
                return numericInputValidation;
            }
            numericInputValidation.min = -999.9999;
            numericInputValidation.max = 999.9999;
            numericInputValidation.step = 0.0001;
            return numericInputValidation;
        case ConditionalRuleDataTypeEnum.DISTANCE:
            numericInputValidation.max = 9999999;
            numericInputValidation.step = 1;
            numericInputValidation.regex = conditionalRuleConstants.REGEX_FOR_MILES;
            return numericInputValidation;
        case ConditionalRuleDataTypeEnum.YEAR:
            numericInputValidation.max = 999;
            numericInputValidation.step = 1;
            numericInputValidation.regex = conditionalRuleConstants.REGEX_FOR_YEAR;
            return numericInputValidation;
        default:
            return numericInputValidation;
    }
};
export const getConditionalRuleLanguageCode = () => {
    return (window['languageCode'] || Utilities.getLocale()).toLowerCase();
};
export const getLocalLanguageCode = () => {
    const languageCode = getConditionalRuleLanguageCode();
    switch (languageCode) {
        case LocaleCodes.FR_CA.toLowerCase():
            return LocaleCodes.FR_CA;
        case LocaleCodes.ES.toLowerCase():
        case LocaleCodes.ES_ES.toLowerCase():
            return LocaleCodes.ES_ES;
        default:
            return LocaleCodes.EN_US;
    }
};
export const getCurrencySymbol = () => {
    const languageCode = getConditionalRuleLanguageCode();
    return languageCode === LocaleCodes.ES.toLowerCase() ||
        languageCode === LocaleCodes.ES_ES.toLowerCase()
        ? CurrencySymbols.EURO_SYMBOL
        : CurrencySymbols.DOLLER_SYMBOL;
};
export const isValidInputValue = (inputValue, dataType, thresholds, cardTitle, translateFn = defaultTranslateFn) => {
    const { regex } = getMinMaxNumericInputValue(dataType, thresholds, cardTitle, translateFn);
    return RegExp(regex).test(inputValue);
};
export const getLabelForCA = (isCanadaRegion, text) => {
    const { STATE_LABEL, PROVINCE_LABEL, ZIP_CODE_LABEL, POSTAL_CODE_LABEL } = conditionalRuleConstants.STATE_ZIP_CODE_VARIABLES;
    if (isCanadaRegion) {
        let label = EMPTY_TEXT;
        switch (text) {
            case STATE_LABEL:
                label = PROVINCE_LABEL;
                break;
            case `${STATE_LABEL}(S)`:
                label = `${PROVINCE_LABEL}(S)`;
                break;
            case ZIP_CODE_LABEL:
                label = POSTAL_CODE_LABEL;
                break;
            case `${ZIP_CODE_LABEL}(S)`:
                label = `${POSTAL_CODE_LABEL}(S)`;
                break;
            default:
                label = text;
                break;
        }
        return label;
    }
    else {
        return text;
    }
};
export const getDateFormat = () => {
    const languageCode = getConditionalRuleLanguageCode();
    switch (languageCode) {
        case LocaleCodes.FR_FR.toLowerCase():
        case LocaleCodes.FR_CA.toLowerCase():
            return DATE_FORMAT_YYYY_MM_DD_HH_MM_SS_A;
        default:
            return DATE_FORMAT_MM_DD_YYYY_HH_MM_SS_A;
    }
};
export const getDateAndTime = (dateTimeValue) => {
    if (!DateUtilities.isValidDate(new Date(dateTimeValue))) {
        return EMPTY_TEXT;
    }
    const updatedDate = new Date(DateUtilities.parseZone(dateTimeValue, DEFAULT_DATE_TIME_FORMAT));
    return `${DateUtilities.formatDate(updatedDate, getDateFormat())}`;
};
export const findSelectedField = (tabCode, fieldList, sectionCode, fieldCode) => {
    var _a, _b;
    const tabField = fieldList === null || fieldList === void 0 ? void 0 : fieldList.find(x => x.code === tabCode);
    if (!sectionCode) {
        return tabField;
    }
    const sectionField = (_a = tabField === null || tabField === void 0 ? void 0 : tabField.fields) === null || _a === void 0 ? void 0 : _a.find(y => y.code === sectionCode);
    return fieldCode ? (_b = sectionField === null || sectionField === void 0 ? void 0 : sectionField.fields) === null || _b === void 0 ? void 0 : _b.find(option => option.code === fieldCode) : sectionField;
};
export const appendAnyWithDropdownType = (option, dropdownType) => {
    return option === conditionalRuleConstants.ESTIMATEPROFILE_EDITS_VARIABLES.ANY
        ? (option + dropdownType).toUpperCase()
        : option;
};
export const getCommentForWarningLabel = (severity, isCommentRequiredForOptional) => {
    // For non-OPTIONAL severity rules, comments are not applicable
    if (severity !== ComplianceProfileRuleSeverity.OPTIONAL) {
        return ComplianceProfileCommentForWarning.NA;
    }
    // For OPTIONAL severity, determine if comment is required or optional
    return isCommentRequiredForOptional
        ? ComplianceProfileCommentForWarning.REQUIRED
        : ComplianceProfileCommentForWarning.OPTIONAL;
};
//# sourceMappingURL=ComplianceConditionalRulesUtils.js.map